/** Web Push helpers.
 *
 *  `PushManager.subscribe` wants the VAPID public key as raw bytes, but the server
 *  hands it over as the base64url string web-push generates. Newer browsers accept
 *  the string directly; older ones throw, so the conversion is done here rather than
 *  relying on that. */
// Return type is explicitly `Uint8Array<ArrayBuffer>`, not a bare `Uint8Array`:
// since TypeScript 5.7 typed arrays are generic over their buffer, and
// `PushManager.subscribe` wants a view over a real ArrayBuffer — a plain
// `new Uint8Array(length)` widens to `ArrayBufferLike`, which includes
// SharedArrayBuffer and is rejected. Allocating the buffer first pins it.
export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  // base64url → base64: restore the padding and the two substituted characters.
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(normalized);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/** Whether this browser can do Web Push at all.
 *
 *  Three separate requirements, and the app should say which one is missing rather
 *  than a blanket "not supported":
 *   - a service worker (no worker, nothing to receive the push)
 *   - PushManager (Safari only gained it for installed web apps)
 *   - a secure context — https or localhost. This is the one that bites in
 *     deployment: over plain http or a bare IP everything above exists and
 *     registration silently fails. */
export function pushSupport(): { supported: boolean; reason?: 'insecure' | 'unsupported' } {
  if (!window.isSecureContext) return { supported: false, reason: 'insecure' };
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { supported: false, reason: 'unsupported' };
  }
  return { supported: true };
}
